import { followingGerman } from "@/src/features/following/translations";
import { playBannerGerman } from "@/src/features/play-banner/translations";
import { stickerDisplayGerman } from "@/src/features/sticker-display/translations";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import apiClient from "../services/api/axios";
import { BLOCKS_ENDPOINTS } from "../services/blocks/config";
import en from "./en";

/**
 * Remote translation overrides for `lang`, layered over the bundled `en` map.
 *
 * v4: `GET /localization/v4/Key/GetUilmFile` (was `/uilm/v1/Key/GetUilmFile`,
 * which now 404s `Application_Not_Found`). Public read — the `x-blocks-key`
 * header is enough, no session needed, which matters because this runs before
 * login.
 *
 * ⚠ `moduleName: "selise-club"` does not exist in the project's Localization
 * config yet: the call returns 200 with an EMPTY body, so every language silently
 * falls back to the bundled English. The module has to be created and its keys
 * generated in the Blocks portal (`/Module/Gets` currently lists only the Blocks
 * boilerplate modules: common, auth, profile, iam, …). Pointing this at `common`
 * instead would pull in Blocks' own key set, not the app's — so the name stays.
 */
const fetchTranslations = async (lang: string) => {
  try {
    const res = await apiClient.get(BLOCKS_ENDPOINTS.uilmFile, {
      params: {
        projectKey: process.env.EXPO_PUBLIC_X_BLOCKS_KEY,
        moduleName: "SELISE_CLUB",
        language: lang,
      },
    });
    return res.data;
  } catch {
    return null;
  }
};

// fetch translations only if preferredLanguage is not en-US
const initI18n = async (preferredLanguage?: string) => {
  if (i18n.isInitialized) return;

  const lang = preferredLanguage ?? "en-US";
  const remote = lang === "en-US" ? null : await fetchTranslations(lang);

  await i18n.use(initReactI18next).init({
    resources: {
      "en-US": { translation: en },
      "de-DE": { translation: { ...en, ...followingGerman, ...playBannerGerman, ...stickerDisplayGerman } },
      [lang]: {
        translation: {
          ...en,
          ...(lang.startsWith("de") ? { ...followingGerman, ...playBannerGerman, ...stickerDisplayGerman } : {}),
          ...remote,
        },
      },
    },
    lng: lang,
    fallbackLng: "en-US",
    react: { useSuspense: false },
    interpolation: { escapeValue: false },
  });
};

export const changeLanguage = async (lang: string) => {
  const remote = lang === "en-US" ? null : await fetchTranslations(lang);
  i18n.addResourceBundle(
    lang,
    "translation",
    { ...en, ...(lang.startsWith("de") ? { ...followingGerman, ...playBannerGerman, ...stickerDisplayGerman } : {}), ...remote },
    true,
    true,
  );
  await i18n.changeLanguage(lang);
};

export { initI18n };
export default i18n;
